import {
  forwardRef,
  useCallback,
  useRef,
  useImperativeHandle,
  useState,
  useEffect
} from 'react'

import { cn } from '@/utils'

import { Input, TInputProps } from '../Input'

export type TMoneyInputProps = Omit<
  TInputProps,
  'type' | 'leftIcon' | 'value' | 'onChange' | 'onBlur'
> & {
  value?: number | string
  defaultValue?: number | string
  onChange?: (value: number | null) => void
  onBlur?: (value: number | null) => void
  allowNegative?: boolean
  maxValue?: number
  minValue?: number
}

/**
 * MoneyInput component for handling monetary values with USD formatting
 *
 * Features:
 * - Currency mask with right-to-left input (like a cash register)
 * - Automatic USD formatting with $ prefix and thousand separators ($1,234.56)
 * - Always displays 2 decimal places
 * - Type digits from right to left: "1" → "$0.01", "12" → "$0.12", "123" → "$1.23"
 * - Backspace removes digits from right to left
 * - Integrates with react-hook-form
 * - Returns numeric value (number) while displaying formatted string
 *
 * @example
 * ```tsx
 * // Uncontrolled
 * <MoneyInput placeholder="$0.00" />
 *
 * // Controlled
 * const [amount, setAmount] = useState(0)
 * <MoneyInput value={amount} onChange={setAmount} />
 *
 * // With react-hook-form
 * const { register } = useForm()
 * <MoneyInput {...register('amount', { valueAsNumber: true })} />
 *
 * // Typing behavior:
 * // Type "1" → displays "$0.01"
 * // Type "2" → displays "$0.12"
 * // Type "3" → displays "$1.23"
 * // Type "4" → displays "$12.34"
 * // Type "5" → displays "$123.45"
 * // Type "6" → displays "$1,234.56"
 * ```
 */
export const MoneyInput = forwardRef<HTMLInputElement, TMoneyInputProps>(
  (
    {
      value: propValue,
      defaultValue: propDefaultValue,
      onChange: propOnChange,
      onBlur: propOnBlur,
      allowNegative = false,
      maxValue,
      minValue,
      placeholder = '$0.00',
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    // Store value as cents (integer) to avoid floating point issues
    const [cents, setCents] = useState<number>(() => {
      const initial = propValue ?? propDefaultValue
      if (initial === null || initial === undefined || initial === '') return 0
      const numValue =
        typeof initial === 'string' ? parseFloat(initial) : initial
      return isNaN(numValue) ? 0 : Math.round(numValue * 100)
    })

    /**
     * Converts cents to formatted display string with $ prefix
     * Example: 12345 cents -> "$123.45"
     */
    const formatCentsToDisplay = useCallback((centsValue: number): string => {
      if (centsValue === 0) return '$0.00'

      const isNegative = centsValue < 0
      const absCents = Math.abs(centsValue)

      // Convert cents to dollars with 2 decimals
      const dollars = (absCents / 100).toFixed(2)

      // Split into integer and decimal parts
      const parts = dollars.split('.')

      // Add thousand separators to integer part
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

      const formatted = parts.join('.')
      return isNegative ? `-$${formatted}` : `$${formatted}`
    }, [])

    /**
     * Converts numeric value to cents
     */
    const valueToCents = useCallback(
      (value: number | null | undefined): number => {
        if (value === null || value === undefined) return 0
        return Math.round(value * 100)
      },
      []
    )

    /**
     * Converts cents to numeric value
     */
    const centsToValue = useCallback(
      (centsValue: number): number => centsValue / 100,
      []
    )

    /**
     * Force cursor to stay at the end (right side)
     */
    const resetCursor = useCallback(() => {
      if (inputRef.current) {
        setTimeout(() => {
          if (inputRef.current) {
            const length = inputRef.current.value.length
            inputRef.current.setSelectionRange(length, length)
          }
        }, 0)
      }
    }, [])

    /**
     * Handles input changes with currency mask (right-to-left)
     */
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value

        // Allow empty input
        if (inputValue === '') {
          setCents(0)
          if (propOnChange) propOnChange(null)
          resetCursor()
          return
        }

        // Remove all non-digit characters except minus sign
        let cleaned = inputValue.replace(/[^\d-]/g, '')

        // Handle negative sign
        const isNegative = cleaned.startsWith('-') && allowNegative
        cleaned = cleaned.replace(/-/g, '')

        // Only allow digits
        if (!/^\d*$/.test(cleaned)) {
          resetCursor()
          return
        }

        // Parse as cents
        let newCents = parseInt(cleaned || '0', 10)
        if (isNegative) newCents = -newCents

        // Apply max constraint (convert to cents)
        if (maxValue !== undefined) {
          const maxCents = Math.round(maxValue * 100)
          if (newCents > maxCents) newCents = maxCents
        }

        // Apply min constraint (convert to cents)
        if (minValue !== undefined) {
          const minCents = Math.round(minValue * 100)
          if (newCents < minCents) newCents = minCents
        }

        setCents(newCents)

        // Notify parent with decimal value
        const decimalValue = centsToValue(newCents)
        if (propOnChange) {
          propOnChange(decimalValue === 0 ? null : decimalValue)
        }

        // Reset cursor to beginning
        resetCursor()
      },
      [
        propOnChange,
        allowNegative,
        maxValue,
        minValue,
        centsToValue,
        resetCursor
      ]
    )

    /**
     * Handles blur event
     */
    const handleBlur = useCallback(() => {
      const decimalValue = centsToValue(cents)
      if (propOnBlur) {
        propOnBlur(decimalValue === 0 ? null : decimalValue)
      }
    }, [propOnBlur, cents, centsToValue])

    /**
     * Handles key down for backspace, delete, and prevent cursor movement
     */
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Allow modifier key combos (Cmd+A, Ctrl+C, etc.)
        if (e.metaKey || e.ctrlKey) return

        // Prevent cursor movement with arrow keys, home, end
        if (
          [
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
            'Home',
            'End'
          ].includes(e.key)
        ) {
          e.preventDefault()
          resetCursor()
          return
        }

        if (e.key === 'Backspace') {
          e.preventDefault()

          // If all text is selected (Cmd+A), clear entirely
          if (inputRef.current) {
            const { selectionStart, selectionEnd, value } = inputRef.current
            if (selectionStart === 0 && selectionEnd === value.length) {
              setCents(0)
              if (propOnChange) propOnChange(null)
              resetCursor()
              return
            }
          }

          const isNegative = cents < 0
          const newCents = Math.floor(Math.abs(cents) / 10)
          const finalCents = isNegative ? -newCents : newCents
          setCents(finalCents)

          const decimalValue = centsToValue(finalCents)
          if (propOnChange) {
            propOnChange(decimalValue === 0 ? null : decimalValue)
          }
          resetCursor()
        } else if (e.key === 'Delete') {
          e.preventDefault()
          setCents(0)
          if (propOnChange) propOnChange(null)
          resetCursor()
        }
      },
      [cents, propOnChange, centsToValue, resetCursor]
    )

    /**
     * Prevent mouse wheel from changing the value
     */
    const handleWheel = useCallback((e: React.WheelEvent<HTMLInputElement>) => {
      e.currentTarget.blur()
    }, [])

    /**
     * Handle focus - reset cursor position
     */
    const handleFocus = useCallback(() => {
      resetCursor()
    }, [resetCursor])

    /**
     * Handle click - prevent cursor position change
     */
    const handleClick = useCallback(() => {
      resetCursor()
    }, [resetCursor])

    /**
     * Handle select - prevent partial selection but allow select-all (Cmd+A / Ctrl+A)
     */
    const handleSelect = useCallback(() => {
      if (inputRef.current) {
        const { selectionStart, selectionEnd, value } = inputRef.current
        if (selectionStart === 0 && selectionEnd === value.length) return
      }
      resetCursor()
    }, [resetCursor])

    // Sync with external value changes (controlled mode)
    useEffect(() => {
      if (propValue !== undefined) {
        const numValue =
          typeof propValue === 'string' ? parseFloat(propValue) : propValue
        const newCents = valueToCents(numValue)
        setCents(newCents)
      }
    }, [propValue, valueToCents])

    // Format display value
    const displayValue = formatCentsToDisplay(cents)

    return (
      <Input
        {...props}
        ref={inputRef}
        type='text'
        inputMode='numeric'
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onWheel={handleWheel}
        onFocus={handleFocus}
        onClick={handleClick}
        onSelect={handleSelect}
        className={cn('!px-4', props.className)}
      />
    )
  }
)

MoneyInput.displayName = 'MoneyInput'
